/*
  Bed product page (sections/mj-bed-product.liquid), October 2026. No dependencies.

  Options and price, stock and preorder, the gallery and full-screen images, mattresses and extras
  (snippets/mj-bed-addons.liquid), the matching protector, the running total (button, sticky bar and
  Clearpay line), Add to Cart and the "Added to your basket" confirmation, tabs and pop-ups.

  Add to Cart posts the bed and everything ticked to /cart/add.json in one request (as the old page did). The line properties
  are the ones the old page sent, which the basket (snippets/mj-cart-line.liquid, mj-cart-chosen.liquid)
  and the order exports rely on:
    bed:     _product_group_id, _is_upsell ("true" when anything was added with it), _upsell_for (null),
             _upsell_to (the add-on variant ids, comma separated, or null), and _Preorder when preordering
    add-on:  _product_group_id (same as the bed), _is_upsell "true", _upsell_for (bed variant id),
             _upsell_to null, _addon_group_index and _addon_category_index (positions in the bed's
             custom.new_upsell groups; category null for assembly and extras), _addon_category_title,
             _upsell_source, _upsell_main_variant_id, _upsell_main_variant_title (the bed's size)
  _upsell_source is "_bed_page_v2_mobile" or "_bed_page_v2_desktop" (991px wide or less counts as
  mobile), and "_bed_page_v2_protector_mobile" / "_desktop" for protectors, so the new page's attach
  rate can be told apart from the old page's "_control_upsell_*" lines.

  v18 (12 Oct 2026), with assets/mj-express.js: the express buttons carry the delivery date the basket would
  pick, a hint says express comes next while extras are ticked, the add sends that date with the lines when the
  basket was empty and asks for sections/mj-express-buttons.liquid, and the pop-up shows the basket's express
  buttons once the basket has its date.
  v21.2 (17 Oct 2026): the pop-up's Checkout and its tap counting are in assets/mj-express.js (MJExpress.shown).
*/
(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  // Shopify customer events (Settings > Customer events); the MJ custom pixel forwards them to GA4
  const track = (name, payload) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (error) { /* tracking must never break the page */ }
  };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const device = () => (window.matchMedia('(max-width: 991px)').matches ? 'mobile' : 'desktop');
  const SOURCE = '_bed_page_v2_';

  /* ---------- Money ---------- */
  function formatMoney(cents) {
    const format = String(window.money_format || '£{{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const number = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return number(0);
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_with_apostrophe_separator': return number(2, "'", '.');
        default: return number(2);
      }
    });
  }

  /* ---------- Sizes (same rules as snippets/mj-size-key.liquid and mj-mattress-size.liquid) ---------- */
  function sizeKey(value) {
    let s = String(value == null ? '' : value).toLowerCase().trim();
    if (s.includes('(')) s = s.split('(')[0].trim();
    s = s.replace(/european/g, 'continental').replace(/euro /g, 'continental ').replace(/eu /g, 'continental ');
    if (s.slice(0, 3) === 'uk ') s = s.slice(3);
    s = s.replace(/[-_]/g, ' ').replace(/['’"]/g, '').replace(/ /g, '');
    const imperial = {
      '3ft': 'single', '3ft0': 'single', '3foot': 'single',
      '2ft6': 'smallsingle', '2ft6in': 'smallsingle',
      '4ft': 'smalldouble', '4ft0': 'smalldouble', '4foot': 'smalldouble',
      '4ft6': 'double', '4ft6in': 'double',
      '5ft': 'king', '5ft0': 'king', '5foot': 'king', kingsize: 'king',
      '6ft': 'superking', '6ft0': 'superking', '6foot': 'superking', superkingsize: 'superking',
    };
    return imperial[s] || s;
  }

  function sizeDims(label) {
    const t = String(label || '').toLowerCase().replace(/-/g, ' ').replace(/×/g, 'x');
    if (t.includes(' over ')) return '';
    const n = t.replace(/continental/g, 'euro').replace(/european/g, 'euro').replace(/ /g, '').replace(/cm/g, '');
    const table = [
      ['superking', '180x200', 180, 200], ['euroking', '160x200', 160, 200], ['eurodouble', '140x200', 140, 200],
      ['eurosmalldouble', '120x200', 120, 200], ['eurosmallsingle', '80x200', 80, 200], ['eurosingle', '90x200', 90, 200],
      ['king', '150x200', 150, 200], ['smalldouble', '120x190', 120, 190], ['double', '135x190', 135, 190],
      ['shorty', '75x175', 75, 175], ['smallsingle', '75x190', 75, 190], ['single', '90x190', 90, 190],
    ];
    const hit = table.find(([word, cm]) => n.includes(word) || n.includes(cm));
    return hit ? `${hit[2]} x ${hit[3]} cm` : '';
  }

  /* ---------- Dialogs: [data-mjb-open-dialog] opens, [data-mjb-close-dialog] or the backdrop closes ---------- */
  function openDialog(dialog, opener) {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('mjb-no-scroll');
    return true;
  }

  if (!window.__mjbDialogs) {
    window.__mjbDialogs = true;
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (target instanceof HTMLDialogElement && target.open && (target.classList.contains('mjb-modal') || target.classList.contains('mjb-lightbox'))) {
        const box = target.getBoundingClientRect();
        const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside || target.classList.contains('mjb-lightbox')) target.close();
        return;
      }
      const opener = target.closest('[data-mjb-open-dialog]');
      if (opener) {
        event.preventDefault();
        openDialog(document.getElementById(opener.dataset.mjbOpenDialog), opener);
        return;
      }
      const closer = target.closest('[data-mjb-close-dialog]');
      if (closer) {
        const dialog = closer.closest('dialog');
        if (dialog) dialog.close();
      }
    });
    document.addEventListener('close', (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      if (!dialog.classList.contains('mjb-modal') && !dialog.classList.contains('mjb-lightbox')) return;
      if (!document.querySelector('dialog.mjb-modal[open], dialog.mjb-lightbox[open]')) document.documentElement.classList.remove('mjb-no-scroll');
      if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
    }, true);
  }

  /* ---------- Tabs and questions: button[aria-controls] inside [data-mjb-acc] ---------- */
  function setAcc(button, open) {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    button.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    button.parentElement.classList.toggle('is-open', open);
    if (open) panel.dispatchEvent(new CustomEvent('mjb:open', { bubbles: true }));
  }

  if (!window.__mjbAccordions) {
    window.__mjbAccordions = true;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-mjb-acc] > button[aria-controls]');
      if (!button || !button.closest('.mjb')) return;
      setAcc(button, button.getAttribute('aria-expanded') !== 'true');
    });
  }

  function headerOffset() {
    const header = document.querySelector('.header-sticky.is-sticky, header.sticky, .sticky-header');
    return (header ? header.getBoundingClientRect().height : 70) + 12;
  }

  function openTab(section, name) {
    const tab = $(`[data-mjb-tab="${name}"]`, section);
    if (!tab) return;
    const button = $(':scope > button[aria-controls]', tab);
    if (button) setAcc(button, true);
    const top = tab.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  /* ---------- Gallery ---------- */
  function initGallery(section) {
    const slides = $('[data-mjb-slides]', section);
    if (!slides) return { goTo() {} };
    const slideEls = $$('[data-media-id]', slides);
    const thumbs = $$('[data-mjb-media-target]', section);
    const counter = $('[data-mjb-count]', section);
    const badge = $('[data-mjb-badge]', section);
    let current = Math.max(0, (Number(counter && counter.textContent) || 1) - 1);

    const mark = (index) => {
      current = index;
      if (counter) counter.textContent = String(index + 1);
      if (badge) badge.hidden = index !== 0;
      const id = slideEls[index] && slideEls[index].dataset.mediaId;
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.mjbMediaTarget === id;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          const strip = thumb.parentElement;
          if (strip && strip.scrollWidth > strip.clientWidth) {
            const offset = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
            strip.scrollTo({ left: Math.max(0, offset), behavior: reducedMotion() ? 'auto' : 'smooth' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    };

    const goToIndex = (index, smooth = true) => {
      const total = slideEls.length;
      if (!total) return;
      const i = ((index % total) + total) % total;
      const slide = slideEls[i];
      const left = slide.getBoundingClientRect().left - slides.getBoundingClientRect().left + slides.scrollLeft;
      slides.scrollTo({ left, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
      mark(i);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = slideEls.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.mjbMediaTarget);
        if (index >= 0) goToIndex(index);
      });
    });
    $$('[data-mjb-step]', section).forEach((button) => {
      button.addEventListener('click', () => goToIndex(current + Number(button.dataset.mjbStep)));
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          const index = slideEls.indexOf(entry.target);
          if (index >= 0 && index !== current) mark(index);
        });
      }, { root: slides, threshold: [0.6] });
      slideEls.forEach((slide) => observer.observe(slide));
    }

    // Full-screen images
    const lightbox = $('[data-mjb-lightbox]', section);
    const lbSlides = lightbox && $('[data-mjb-lightbox-slides]', lightbox);
    const lbGo = (index, smooth) => {
      if (!lbSlides) return;
      const items = $$('[data-index]', lbSlides);
      if (!items.length) return;
      let pos = items.findIndex((item) => Number(item.dataset.index) === index);
      if (pos < 0) pos = 0;
      lbSlides.scrollTo({ left: items[pos].offsetLeft, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    };
    $$('[data-mjb-zoom]', section).forEach((button) => {
      button.addEventListener('click', () => {
        if (!lightbox) return;
        $$('img[loading="lazy"]', lightbox).forEach((img) => { img.loading = 'eager'; });
        openDialog(lightbox, button);
        requestAnimationFrame(() => lbGo(Number(button.dataset.mjbZoom), false));
      });
    });
    if (lightbox && lbSlides) {
      $$('[data-mjb-lb-step]', lightbox).forEach((button) => {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          const width = lbSlides.clientWidth || 1;
          lbSlides.scrollBy({ left: Number(button.dataset.mjbLbStep) * width, behavior: reducedMotion() ? 'auto' : 'smooth' });
        });
      });
      lbSlides.addEventListener('click', (event) => event.stopPropagation());
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          lbSlides.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * lbSlides.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      });
    }

    return {
      goTo(mediaId) {
        const index = slideEls.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) goToIndex(index);
      },
    };
  }

  /* ---------- Product ---------- */
  function initSection(section) {
    if (!section || section._mjbInit) return;
    section._mjbInit = true;

    let data;
    try {
      data = JSON.parse($('[data-mjb-product-json]', section).textContent);
    } catch (error) {
      return;
    }
    const variants = data.variants || [];
    const buttonLabel = data.buttonLabel || 'Add to basket';
    const sizeIndex = typeof data.sizeIndex === 'number' ? data.sizeIndex : -1;
    const gallery = initGallery(section);

    const form = $('form.mjb-form', section);
    const idInput = form && $('[data-mjb-variant-id]', form);
    const addButton = form && $('[data-mjb-add]', form);
    const addLabel = addButton && $('[data-mjb-add-label]', addButton);
    const addTotal = addButton && $('[data-mjb-add-total]', addButton);
    const summary = form && $('[data-mjb-summary]', form);
    const errorBox = form && $('[data-mjb-error]', form);
    const dynamic = form && $('[data-mjb-dynamic]', form);
    // v18: the express buttons' delivery date, and the hint while extras are ticked (assets/mj-express.js)
    const expressHint = form && $('[data-mj-express-hint]', form);
    const dateRules = window.MJExpress ? window.MJExpress.rulesIn(section) : null;
    let expressVariant = null;
    const express = window.MJExpress && form ? window.MJExpress.product({
      form,
      rules: dateRules,
      note: $('[data-mj-express-note]', form),
      express: dynamic,
      variant: () => expressVariant,
    }) : null;
    const price = $('[data-mjb-price]', section);
    const stock = $('[data-mjb-stock]', section);
    const etaSlot = $('[data-mjb-eta-slot]', section);
    const sticky = $('[data-mjb-sticky]', section);
    const stickyButton = sticky && $('[data-mjb-sticky-add]', sticky);
    const fieldsets = $$('[data-mjb-option]', section);
    const addons = $('[data-mjb-addons]', section);
    const protector = addons && $('[data-mjb-protector]', addons);
    const protectorInput = protector && $('[data-mjb-protector-input]', protector);
    let protectorData = null;
    try {
      const json = protector && $('[data-mjb-protector-json]', protector);
      protectorData = json ? JSON.parse(json.textContent) : null;
    } catch (error) {
      protectorData = null;
    }

    let currentVariant = variants.find((v) => idInput && String(v.id) === idInput.value) || variants[0];
    let busy = false;

    /* --- Options --- */
    const selectedOptions = () => {
      const options = (currentVariant ? currentVariant.options : []).slice();
      fieldsets.forEach((fieldset) => {
        const index = Number(fieldset.dataset.mjbOption);
        const checked = $('input:checked', fieldset);
        if (checked) options[index] = checked.value;
      });
      return options;
    };
    const findVariant = (options) => variants.find((variant) => variant.options.every((value, i) => value === options[i]));

    function markOptions(options) {
      fieldsets.forEach((fieldset) => {
        const index = Number(fieldset.dataset.mjbOption);
        const selected = $('[data-mjb-option-selected]', fieldset);
        if (selected) selected.textContent = options[index] || '';
        const prices = new Set();
        $$('input', fieldset).forEach((input) => {
          const test = options.slice();
          test[index] = input.value;
          const match = findVariant(test);
          const label = $(`label[for="${input.id}"]`, fieldset);
          if (!label) return;
          label.classList.toggle('is-unavailable', !match || !match.available);
          const status = $('[data-mjb-pill-status]', label);
          if (status) status.textContent = !match ? ' (unavailable)' : (match.available ? '' : ' (sold out)');
          const pillPrice = $('[data-mjb-pill-price]', label);
          if (pillPrice) pillPrice.textContent = match ? formatMoney(match.price) : '';
          if (match) prices.add(match.price);
        });
        // Prices on the buttons only when this option's choices cost different amounts
        $$('[data-mjb-pill-price]', fieldset).forEach((el) => { el.hidden = prices.size < 2; });
      });
    }

    function renderPrice(variant) {
      if (!price) return;
      const current = $('[data-mjb-price-current]', price);
      const was = $('[data-mjb-price-was]', price);
      const compare = $('[data-mjb-price-compare]', price);
      const save = $('[data-mjb-price-save]', price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (current) current.textContent = formatMoney(variant.price);
      price.classList.toggle('is-sale', Boolean(onSale));
      if (was) was.hidden = !onSale;
      if (compare) compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      if (save) {
        save.hidden = !onSale;
        // Whole pounds and the percentage, rounded down: "Save £259 (24%)"
        const saveCents = variant.compare_at_price - variant.price;
        const savePct = Math.floor((saveCents * 100) / variant.compare_at_price);
        const saveText = saveCents < 100 ? formatMoney(saveCents) : `£${Math.floor(saveCents / 100).toLocaleString('en-GB')}`;
        save.textContent = onSale ? `Save ${saveText}${savePct >= 1 ? ` (${savePct}%)` : ''}` : '';
      }
    }

    function renderStock(variant) {
      const state = variant ? variant.stock || '' : '';
      if (stock) {
        const text = $('[data-mjb-stock-text]', stock);
        const words = {
          in: 'In stock',
          low: 'Low stock',
          very_low: 'Very low stock',
          preorder: data.preorderDate ? `Limited stock arriving ${data.preorderDate}. Preorder to secure one` : 'Available to preorder',
          out: 'Out of stock',
        };
        stock.hidden = !state;
        stock.className = `mjb-stock mjb-stock--${state || 'none'}`;
        if (text) text.textContent = words[state] || '';
      }
      // No delivery date under the price when the variant is sold out or on preorder: the stock line says what happens
      if (etaSlot) etaSlot.classList.toggle('is-no-eta', !variant || !variant.available || state === 'preorder');
      // The preorder date goes on the bed's line, as the old page meant to do
      let input = form && $('[data-mjb-preorder-input]', form);
      if (state === 'preorder' && data.preorderDate) {
        if (!input && form) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[_Preorder]';
          input.dataset.mjbPreorderInput = '';
          form.appendChild(input);
        }
        if (input) input.value = `Est. ${data.preorderDate}`;
      } else if (input) {
        input.remove();
      }
      return state;
    }

    function setButtons(enabled, text) {
      if (addons) addons.hidden = !enabled;
      if (addButton) addButton.disabled = !enabled;
      if (addLabel) addLabel.textContent = text;
      if (addTotal) addTotal.hidden = !enabled;
      if (stickyButton) {
        stickyButton.disabled = !enabled;
        stickyButton.textContent = text;
      }
    }

    /* --- Add-ons ---
       A single category is a list of rows ([data-mjb-opt], one tick each). Categories that offer the
       same mattresses share cards ([data-mjb-card]) with a tick button per category ([data-mjb-chip]).
       Each tick input keeps the variant it adds in input._match. */
    const catRows = (cat) => $$(':scope > .mjb-cat__list > [data-mjb-opt]:not([data-mjb-card])', cat);
    const catCards = (cat) => $$(':scope > .mjb-cat__list > [data-mjb-card]', cat);
    const parseVariants = (el) => {
      if (!el._variants) {
        try {
          el._variants = JSON.parse(el.dataset.variants || '[]');
        } catch (error) {
          el._variants = [];
        }
      }
      return el._variants;
    };
    const namesOf = (el) => (el.dataset.fn || '').split('|').map((n) => n.trim()).filter(Boolean).map(sizeKey);
    function pickVariant(list, vf, nameKeys, key) {
      const ok = list.filter((v) => v.a);
      if (vf) return key ? ok.find((v) => v.k === key) || null : null;
      if (nameKeys.length) return ok.find((v) => nameKeys.includes(v.k)) || null;
      return ok[0] || null;
    }

    function setInputVariant(input, holder, match) {
      if (!input) return;
      input._match = match || null;
      if (!match) {
        if (input.checked) input.checked = false;
        input.value = '';
        input.dataset.price = '';
        return;
      }
      input.value = String(match.id);
      input.dataset.price = String(match.p);
      input.dataset.variantTitle = match.t;
      const priceEl = $('[data-mjb-opt-price]', holder);
      const compareEl = $('[data-mjb-opt-compare]', holder);
      const sr = $('[data-mjb-opt-sr]', holder);
      if (priceEl) priceEl.textContent = match.p === 0 ? 'Free' : `+${formatMoney(match.p)}`;
      if (sr) sr.textContent = match.p === 0 ? 'free' : formatMoney(match.p);
      if (compareEl) {
        const onSale = match.c && match.c > match.p;
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? `${compareEl.dataset.rrp === 'true' ? 'RRP ' : ''}${formatMoney(match.c)}` : '';
      }
    }

    // A trundle mattress only makes sense when the trundle is in ("Bed Only" leaves it out)
    const trundleOptions = Array.isArray(data.trundleOptions) ? data.trundleOptions : [];
    const trundleLeftOut = () => trundleOptions.some((index) => {
      const value = String((currentVariant && currentVariant.options[index]) || '').toLowerCase();
      return !value.includes('trundle') || value.includes('no trundle');
    });

    function setMore(cat, liveCount, hiddenCount, expanded) {
      const more = $(':scope > [data-mjb-more]', cat);
      if (!more) return;
      const label = $('[data-mjb-more-label]', more);
      if (expanded) {
        more.hidden = liveCount < 2;
        more.setAttribute('aria-expanded', 'true');
        if (label) label.textContent = 'Show fewer';
      } else {
        more.hidden = hiddenCount === 0;
        more.setAttribute('aria-expanded', 'false');
        if (label) label.textContent = `${hiddenCount} more ${hiddenCount === 1 ? 'option' : 'options'}`;
      }
    }

    function layoutCategory(cat) {
      const trundleOut = trundleLeftOut();
      const expanded = cat.dataset.expanded === 'true';
      if (cat.hasAttribute('data-mjb-shared')) {
        const cards = catCards(cat);
        cards.forEach((card) => {
          let any = false;
          $$('[data-mjb-chip]', card).forEach((chip) => {
            const input = $('[data-mjb-addon]', chip);
            const off = !input || !input._match || (chip.hasAttribute('data-trundle') && trundleOut);
            if (off && input && input.checked) input.checked = false;
            chip.hidden = off;
            if (!off) any = true;
          });
          card._live = any;
        });
        const live = cards.filter((card) => card._live);
        const primary = live.find((card) => card.hasAttribute('data-badged')) || live[0];
        cards.forEach((card) => {
          const checked = $$('[data-mjb-addon]:checked', card).length > 0;
          card.hidden = !card._live || (!expanded && card !== primary && !checked);
        });
        setMore(cat, live.length, live.filter((card) => card.hidden).length, expanded);
        cat.hidden = live.length === 0;
        return;
      }
      const rows = catRows(cat);
      if (cat.hasAttribute('data-trundle') && trundleOut) {
        rows.forEach((row) => {
          const input = $('[data-mjb-addon]', row);
          if (input) input.checked = false;
        });
        cat.hidden = true;
        return;
      }
      const matching = rows.filter((row) => {
        const input = $('[data-mjb-addon]', row);
        return input && input._match;
      });
      const primary = matching[0];
      rows.forEach((row) => {
        const input = $('[data-mjb-addon]', row);
        const live = input && input._match;
        row.hidden = !live || (!expanded && row !== primary && !input.checked);
      });
      setMore(cat, matching.length, matching.filter((row) => row.hidden).length, expanded);
      cat.hidden = matching.length === 0;
    }

    function updateAddons(sizeValue) {
      if (!addons) return;
      const key = sizeKey(sizeValue);
      const dims = sizeDims(sizeValue);
      const sizeText = sizeValue ? (dims ? `${sizeValue}, ${dims}` : sizeValue) : '';
      $$('[data-mjb-cat]', addons).forEach((cat) => {
        const sizeLabel = $(':scope > legend [data-mjb-cat-size]', cat);
        if (cat.hasAttribute('data-mjb-shared')) {
          let anyVf = false;
          catCards(cat).forEach((card) => {
            const list = parseVariants(card);
            $$('[data-mjb-chip]', card).forEach((chip) => {
              const vf = chip.dataset.vf === 'true';
              if (vf) anyVf = true;
              setInputVariant($('[data-mjb-addon]', chip), chip, pickVariant(list, vf, namesOf(chip), key));
            });
          });
          if (sizeLabel && anyVf && !sizeLabel.hidden) sizeLabel.textContent = sizeText;
        } else {
          const vf = cat.dataset.vf === 'true';
          const nameKeys = namesOf(cat);
          catRows(cat).forEach((row) => {
            setInputVariant($('[data-mjb-addon]', row), row, pickVariant(parseVariants(row), vf, nameKeys, key));
          });
          if (sizeLabel && vf) sizeLabel.textContent = sizeText;
        }
        layoutCategory(cat);
      });
      $$('[data-mjb-simple]', addons).forEach((group) => {
        $$('[data-mjb-opt]', group).forEach((row) => {
          const list = parseVariants(row).filter((v) => v.a);
          const match = (key && list.find((v) => v.k === key)) || list[0] || null;
          setInputVariant($('[data-mjb-addon]', row), row, match);
          row.hidden = !match;
        });
      });
      updateProtector();
    }

    function updateProtector() {
      if (!protector || !protectorInput || !protectorData) return;
      const sizes = [];
      $$('[data-mjb-addon][data-mattress]:checked', addons).forEach((input) => {
        if (input._match && !input.closest('[hidden]')) sizes.push(input._match);
      });
      const picks = [];
      sizes.forEach((m) => {
        const pv = (protectorData.variants || []).find((v) => v.a && v.k === m.k);
        if (pv) picks.push(pv);
      });
      protector._picks = picks;
      if (!picks.length) {
        protector.hidden = true;
        protectorInput.checked = false;
        return;
      }
      protector.hidden = false;
      const name = $('[data-mjb-protector-name]', protector);
      const size = $('[data-mjb-protector-size]', protector);
      const cost = $('[data-mjb-protector-price]', protector);
      if (!protector._baseTitle && name) protector._baseTitle = name.textContent.trim();
      const base = protector._baseTitle || 'Add a waterproof protector';
      if (name) {
        if (picks.length === 1) name.textContent = base;
        else name.textContent = `${base.replace(/^Add a /i, 'Add ').replace(/protector$/i, 'protectors')} for ${picks.length === 2 ? 'both' : `all ${picks.length}`} mattresses`;
      }
      const uniqueSizes = [...new Set(picks.map((p) => p.t))];
      if (size) size.textContent = uniqueSizes.join(' and ');
      const total = picks.reduce((sum, p) => sum + p.p, 0);
      if (cost) cost.textContent = `+${formatMoney(total)}`;
      const sr = $('[data-mjb-protector-sr]', protector);
      if (sr && name) sr.textContent = `${name.textContent}, ${uniqueSizes.join(' and ')}, ${formatMoney(total)}`;
    }

    function selectedAddons() {
      if (!addons) return [];
      return $$('[data-mjb-addon]:checked', addons)
        .filter((input) => input.value && !input.closest('[hidden]'))
        .map((input) => ({
          id: Number(input.value),
          price: Number(input.dataset.price) || 0,
          kind: input.dataset.kind,
          gi: input.dataset.gi,
          ci: input.dataset.ci,
          ctitle: (input.dataset.ctitle || '').trim(),
          title: input.dataset.title || '',
        }));
    }

    function selectedProtectors() {
      if (!protector || !protectorInput || !protectorInput.checked || protector.hidden) return [];
      return (protector._picks || []).map((p) => ({ id: p.id, price: p.p, size: p.t }));
    }

    /* --- Running total --- */
    function updateTotals() {
      const variant = currentVariant;
      const extras = selectedAddons();
      const prots = selectedProtectors();
      const extrasCount = extras.length + prots.length;
      const extrasTotal = extras.reduce((sum, a) => sum + a.price, 0) + prots.reduce((sum, p) => sum + p.price, 0);
      const base = variant ? variant.price : 0;
      const total = base + extrasTotal;
      if (addTotal) addTotal.textContent = formatMoney(total);
      if (summary) {
        summary.hidden = extrasCount === 0;
        summary.textContent = extrasCount ? `Bed ${formatMoney(base)} plus ${extrasCount} ${extrasCount === 1 ? 'extra' : 'extras'} ${formatMoney(extrasTotal)}` : '';
      }
      if (sticky) {
        const t = $('[data-mjb-sticky-total]', sticky);
        const x = $('[data-mjb-sticky-extras]', sticky);
        if (t) t.textContent = formatMoney(total);
        if (x) {
          x.hidden = extrasCount === 0;
          x.textContent = extrasCount ? `with ${extrasCount} ${extrasCount === 1 ? 'extra' : 'extras'}` : '';
        }
      }
      // Clearpay's line follows the total (its block only follows variant changes on its own)
      $$('square-placement', section).forEach((placement) => {
        placement.setAttribute('data-amount', (total / 100).toFixed(2));
      });
      // Express checkout buys the bed alone, so it steps aside while extras are ticked,
      // and when the chosen variant is sold out (Shop Pay would otherwise still show)
      if (dynamic) dynamic.hidden = extrasCount > 0 || !(variant && variant.available);
      // v18: say where express went, and keep its delivery date in step with the variant
      if (expressHint) expressHint.hidden = !(dynamic && extrasCount > 0 && variant && variant.available);
      expressVariant = variant || null;
      if (express) express.update();
    }

    /* --- Variant photo ---
       A variant with no photo of its own borrows one from the closest variant that has one: the same
       colour first (swatch options), then the most other matching options. Sizes are ignored. */
    const colourIndexes = fieldsets.filter((f) => f.classList.contains('mjb-option--swatch')).map((f) => Number(f.dataset.mjbOption));
    function mediaFor(variant) {
      if (!variant) return null;
      if (variant.featured_media) return variant.featured_media;
      let best = null;
      let bestScore = 0;
      variants.forEach((v) => {
        if (v === variant || !v.featured_media) return;
        let score = 0;
        v.options.forEach((value, i) => {
          if (i === sizeIndex || value !== variant.options[i]) return;
          score += colourIndexes.includes(i) ? 10 : 1;
        });
        if (score > bestScore) {
          best = v;
          bestScore = score;
        }
      });
      return best ? best.featured_media : null;
    }

    /* --- Variant change --- */
    let lastSize = null;
    function update(event) {
      const options = selectedOptions();
      markOptions(options);
      const variant = findVariant(options);
      if (!variant) {
        setButtons(false, 'Unavailable');
        renderStock(null);
        if (dynamic) dynamic.hidden = true;
        if (expressHint) expressHint.hidden = true;
        expressVariant = null;
        if (express) express.update();
        return;
      }
      currentVariant = variant;
      if (idInput && idInput.value !== String(variant.id)) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      renderPrice(variant);
      const state = renderStock(variant);
      if (!variant.available) setButtons(false, 'Sold out');
      else setButtons(true, state === 'preorder' ? 'Preorder' : buttonLabel);
      const size = sizeIndex >= 0 ? variant.options[sizeIndex] : '';
      if (size !== lastSize) {
        lastSize = size;
        updateAddons(size);
      } else if (addons) {
        $$('[data-mjb-cat]', addons).forEach(layoutCategory);
        updateProtector();
      }
      const media = mediaFor(variant);
      if (media && event) gallery.goTo(media);
      if (sticky) {
        const v = $('[data-mjb-sticky-variant]', sticky);
        if (v && variants.length > 1) v.textContent = variant.title;
      }
      updateTotals();
      // Rewrite the address only when the shopper picked something: every URL change counts as a
      // page view in GA4 (history-change tracking).
      if (event && event.isTrusted && data.url) {
        const next = `${data.url}?variant=${variant.id}`;
        if (window.location.pathname + window.location.search !== next) window.history.replaceState(window.history.state, '', next);
      }
      document.dispatchEvent(new CustomEvent('mjb:variant-change', { detail: { variant } }));
    }

    section.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('.mjb-option__input')) {
        update(event);
        return;
      }
      if (target.matches('[data-mjb-addon]')) {
        if (target.checked) {
          if (target.dataset.kind === 'cat') {
            // One mattress per category: untick the others in it (rows, or the same bunk on other cards)
            $$('[data-mjb-addon][data-kind="cat"]', addons).forEach((other) => {
              if (other !== target && other.dataset.gi === target.dataset.gi && other.dataset.ci === target.dataset.ci) other.checked = false;
            });
          } else if (target.dataset.kind === 'simple') {
            const group = target.closest('[data-mjb-simple]');
            if (group && group.dataset.multi === 'false') {
              $$('[data-mjb-addon]', group).forEach((other) => { if (other !== target) other.checked = false; });
            }
          }
        }
        const cat = target.closest('[data-mjb-cat]');
        if (cat) layoutCategory(cat);
        updateProtector();
        updateTotals();
        clearError();
        if (target.checked) {
          track('mj_addon_tick', {
            page: 'bed',
            kind: target.dataset.kind === 'cat' ? 'mattress' : 'extra',
            group: (target.dataset.ctitle || '').trim(),
            item_id: String(target.value || ''),
            item_name: target.dataset.title || '',
            price: (Number(target.dataset.price) || 0) / 100,
            product: data.title || '',
            variant: currentVariant ? currentVariant.title : '',
          });
        }
        return;
      }
      if (target.matches('[data-mjb-protector-input]')) {
        updateTotals();
        if (target.checked) {
          (protector && protector._picks ? protector._picks : []).forEach((p) => {
            track('mj_addon_tick', { page: 'bed', kind: 'protector', group: 'Protector', item_id: String(p.id), item_name: 'Mattress protector', price: (Number(p.p) || 0) / 100, product: data.title || '', variant: p.t || '' });
          });
        }
      }
    });

    section.addEventListener('click', (event) => {
      const more = event.target.closest('[data-mjb-more]');
      if (more) {
        const cat = more.closest('[data-mjb-cat]');
        cat.dataset.expanded = cat.dataset.expanded === 'true' ? 'false' : 'true';
        layoutCategory(cat);
        return;
      }
      const tabLink = event.target.closest('[data-mjb-open-tab]');
      if (tabLink) {
        event.preventDefault();
        openTab(section, tabLink.dataset.mjbOpenTab);
      }
    });

    /* --- Add to Cart --- */
    function clearError() {
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }
    }
    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message || 'Sorry, that didn’t work. Please try again.';
      errorBox.hidden = false;
    }

    function buildItems() {
      const variant = currentVariant;
      const groupId = Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
      const where = device();
      const extras = selectedAddons();
      const prots = selectedProtectors();
      const size = sizeIndex >= 0 ? variant.options[sizeIndex] : variant.title;
      const extraIds = extras.map((a) => a.id).concat(prots.map((p) => p.id));
      const bedProps = {
        _product_group_id: groupId,
        _is_upsell: extraIds.length ? 'true' : 'false',
        _upsell_for: null,
        _upsell_to: extraIds.length ? extraIds.join(',') : null,
      };
      const preorder = form && $('[data-mjb-preorder-input]', form);
      if (preorder && preorder.value) bedProps._Preorder = preorder.value;
      const items = [{ id: variant.id, quantity: 1, properties: bedProps }];
      const common = {
        _product_group_id: groupId,
        _is_upsell: 'true',
        _upsell_for: String(variant.id),
        _upsell_to: null,
      };
      extras.forEach((a) => {
        items.push({
          id: a.id,
          quantity: 1,
          properties: Object.assign({}, common, {
            _addon_group_index: String(a.gi),
            _addon_category_index: a.kind === 'cat' ? String(a.ci) : null,
            _addon_category_title: a.kind === 'cat' ? a.ctitle : null,
            _upsell_source: `${SOURCE}${where}`,
            _upsell_main_variant_id: String(variant.id),
            _upsell_main_variant_title: size,
          }),
        });
      });
      // One protector per mattress; two of the same size go on one line
      const counts = new Map();
      prots.forEach((p) => counts.set(p.id, (counts.get(p.id) || 0) + 1));
      counts.forEach((quantity, id) => {
        items.push({
          id,
          quantity,
          properties: Object.assign({}, common, {
            _addon_category_title: 'Mattress protector',
            _upsell_source: `${SOURCE}protector_${where}`,
            _upsell_main_variant_id: String(variant.id),
            _upsell_main_variant_title: size,
          }),
        });
      });
      return items;
    }

    function refreshHeaderCount(cart) {
      if (typeof window.refreshCart === 'function') {
        try {
          window.refreshCart();
          return;
        } catch (error) { /* fall through */ }
      }
      $$('.ajax-cart-count span[aria-hidden="true"], .bigcounter .count, .cart-count-bubble span').forEach((el) => { el.textContent = String(cart.item_count); });
    }

    // /cart/add.json gives prices in pounds as text ("189.99"), /cart.js in pence: take the basket's line where we can
    const lineCents = (item, cart) => {
      const line = cart && cart.items ? cart.items.find((l) => l.key === item.key) : null;
      if (line && typeof line.final_line_price === 'number') return line.final_line_price;
      if (typeof item.final_line_price === 'number') return item.final_line_price;
      if (typeof item.line_price === 'number') return item.line_price;
      const pounds = parseFloat(item.line_price);
      return Number.isFinite(pounds) ? Math.round(pounds * 100) : 0;
    };

    const lineHtml = (item, cart) => {
      // /cart/add.json sends no image for the lines it adds, so take the picture from the basket line (/cart.js)
      const line = cart && cart.items ? cart.items.find((l) => l.key === item.key) : null;
      const src = (line && (line.image || (line.featured_image && line.featured_image.url))) || item.image || (item.featured_image && item.featured_image.url) || '';
      const img = src ? `${src}${src.includes('?') ? '&' : '?'}width=160` : '';
      const variantTitle = item.variant_title && !/^default title$/i.test(item.variant_title) && !/^(yes please|yes|no|no thanks)$/i.test(item.variant_title) ? item.variant_title : '';
      const linePrice = lineCents(item, cart);
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      return `<li class="mjb-added__line">
        <span class="mjb-added__img">${img ? `<img src="${esc(img)}" alt="" width="56" height="56" loading="eager">` : ''}</span>
        <span class="mjb-added__text"><span class="mjb-added__name">${esc(item.product_title || item.title)}</span>${variantTitle ? `<span class="mjb-added__variant">${esc(variantTitle)}</span>` : ''}${item.quantity > 1 ? `<span class="mjb-added__variant">Quantity ${item.quantity}</span>` : ''}</span>
        <span class="mjb-added__price">${linePrice === 0 ? 'Free' : esc(formatMoney(linePrice))}</span>
      </li>`;
    };

    async function addToCart() {
      if (busy || !currentVariant || !currentVariant.available) return;
      busy = true;
      clearError();
      const items = buildItems();
      // v18: on a bed on the basket's date picker, the date the basket would pick goes on every line when the
      // basket was empty; assets/mj-express.js checks the whole basket once the lines are in
      const mjx = window.MJExpress;
      const sendDate = mjx ? mjx.predict(dateRules, currentVariant.id) : null;
      if (sendDate) items.forEach((item) => { item.properties = Object.assign({}, item.properties, { 'Delivery Date': sendDate }); });
      if (addButton) {
        addButton.classList.add('is-loading');
        addButton.setAttribute('aria-busy', 'true');
      }
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      try {
        // cart/add.json, as the old add-on drawer used (assets/add-on-drawer-new.js), so the store's analytics see the add the same way
        const response = await fetch(`${root}cart/add.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(mjx ? { items, sections: mjx.SECTION } : { items }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          showError(result.description || result.message);
          return;
        }
        let cart = null;
        try {
          cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        } catch (error) {
          cart = null;
        }
        // Shopify lists lines added together newest first; show the bed first
        const added = (result.items || []).slice().sort((a, b) => (a.variant_id === currentVariant.id ? -1 : b.variant_id === currentVariant.id ? 1 : 0));
        showAdded(added, cart);
        // v18: the basket's express buttons in the pop-up, once the basket has its delivery date
        if (mjx) {
          if (cart) mjx.setCart(cart);
          const dialog = $('[data-mjb-added]', section);
          if (dialog) {
            mjx.afterAdd(result.sections && result.sections[mjx.SECTION], {
              root: $('[data-mjb-added-express]', dialog),
              wallets: $('[data-mjb-added-wallets]', dialog),
              note: $('[data-mjb-added-date]', dialog),
            });
          }
        }
        if (cart) refreshHeaderCount(cart);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        // Untick the extras so a second press adds the bed on its own
        if (addons) {
          $$('[data-mjb-addon]:checked', addons).forEach((input) => { input.checked = false; });
          if (protectorInput) protectorInput.checked = false;
          $$('[data-mjb-cat]', addons).forEach(layoutCategory);
          updateProtector();
          updateTotals();
        }
      } catch (error) {
        showError();
      } finally {
        busy = false;
        if (addButton) {
          addButton.classList.remove('is-loading');
          addButton.removeAttribute('aria-busy');
        }
      }
    }

    function showAdded(items, cart) {
      const dialog = $('[data-mjb-added]', section);
      if (!dialog) {
        window.location.href = data.cartUrl || '/cart';
        return;
      }
      const list = $('[data-mjb-added-lines]', dialog);
      if (list) list.innerHTML = items.map((item) => lineHtml(item, cart)).join('');
      const totalRow = $('[data-mjb-added-total]', dialog);
      if (totalRow && cart) {
        const count = $('[data-mjb-added-count]', dialog);
        const subtotal = $('[data-mjb-added-subtotal]', dialog);
        if (count) count.textContent = `Basket subtotal (${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;
        if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
        totalRow.hidden = false;
      }
      // v21.2: Checkout, and counting the pop-up's taps (assets/mj-express.js)
      if (openDialog(dialog, addButton) && window.MJExpress && window.MJExpress.shown) window.MJExpress.shown(dialog, 'bed');
    }

    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        addToCart();
      });
    }

    /* --- Sticky bar: shows once the main button has scrolled up out of view, hides over the footer --- */
    if (sticky && addButton && 'IntersectionObserver' in window) {
      const footer = document.querySelector('[id$="__footer"], .shopify-section-group-footer-group, footer');
      let pastButton = false;
      let overFooter = false;
      document.body.classList.add('mj-wa-gate'); // v16 R5: WhatsApp waits until the button has scrolled past once
      const setShown = () => {
        const show = pastButton && !overFooter;
        if (pastButton) document.body.classList.add('mj-wa-ok');
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('mj-sticky-on', show);
        if (stickyButton) stickyButton.tabIndex = show ? 0 : -1;
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === addButton) pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          else overFooter = entry.isIntersecting;
        });
        setShown();
      });
      observer.observe(addButton);
      if (footer) observer.observe(footer);
      if (stickyButton) {
        stickyButton.addEventListener('click', () => {
          if (addButton.disabled) return;
          addButton.click();
        });
      }
    }

    /* --- Long descriptions fold to a few paragraphs on phones --- */
    const desc = $('[data-mjb-desc]', section);
    const descMore = $('[data-mjb-desc-more]', section);
    if (desc && descMore) {
      const limit = () => (window.matchMedia('(max-width: 989px)').matches ? 380 : 560);
      const fold = () => {
        if (descMore.getAttribute('aria-expanded') === 'true') return;
        const tall = desc.scrollHeight > limit() + 120;
        desc.classList.toggle('is-folded', tall);
        desc.style.maxHeight = tall ? `${limit()}px` : '';
        descMore.hidden = !tall;
      };
      descMore.addEventListener('click', () => {
        const open = descMore.getAttribute('aria-expanded') !== 'true';
        descMore.setAttribute('aria-expanded', String(open));
        desc.classList.toggle('is-folded', !open);
        desc.style.maxHeight = open ? '' : `${limit()}px`;
        descMore.textContent = open ? 'Read less' : 'Read more';
        if (!open) desc.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
      fold();
      window.addEventListener('load', fold, { once: true });
      section.addEventListener('mjb:open', (event) => { if (event.target.contains(desc)) fold(); });
    }

    // Start from what the page shows (a ?variant= address, or a browser that kept an old choice)
    update(null);
  }

  function init(root = document) {
    $$('[data-mjb-product]', root).forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
